<div class="calendar-container p-6 bg-gray-50 min-h-screen">
  <!-- Back & Header -->
  <div class="mb-6 flex flex-col md:flex-row md:items-center md:justify-between pb-4 border-b border-gray-200 gap-4">
    <div>
      <div class="flex items-center gap-2 mb-2">
        <a routerLink="/agents" class="text-sm font-medium text-green-600 hover:text-green-700 flex items-center gap-1">
          <i class="fas fa-arrow-left"></i> Back to Agents
        </a>
        <span class="text-gray-300" *ngIf="agent">|</span>
        <div class="flex items-center gap-3">
          <button *ngIf="agent && selectedDates.length > 0" (click)="clearSelection()" class="text-xs font-semibold text-gray-500 hover:text-rose-600 border border-gray-200 bg-white hover:bg-rose-50 px-2.5 py-1.5 rounded-lg shadow-sm transition-all duration-200">
            <i class="fas fa-times-circle"></i> Clear Selection ({{ selectedDates.length }})
          </button>
          <button *ngIf="agent" (click)="openMarkLeaveModal()" class="text-sm font-medium flex items-center gap-1.5 focus:outline-none px-3.5 py-2 rounded-lg transition-all duration-200 shadow-sm border"
                  [ngClass]="selectedDates.length > 0 ? 'text-white bg-emerald-600 hover:bg-emerald-700 border-emerald-700 font-bold' : 'text-blue-600 hover:text-blue-700 border-transparent bg-transparent hover:bg-blue-50/50 font-medium'">
            <i class="fas fa-calendar-plus"></i>
            <span>{{ selectedDates.length > 0 ? 'Mark Leave for Selected (' + selectedDates.length + ')' : 'Mark Agent Leave' }}</span>
          </button>
        </div>
      </div>
      <h1 class="text-2xl font-bold text-gray-900" *ngIf="agent">Attendance Calendar — {{ agent.name }}</h1>
      <h1 class="text-2xl font-bold text-gray-900" *ngIf="!agent">Attendance Calendar</h1>
      <p class="text-gray-500 text-sm mt-1" *ngIf="agent">Agent ID: <span class="font-mono">{{ agent.agentId }}</span> | Phone: {{ agent.phoneNumber }}</p>
      <p class="text-gray-500 text-sm mt-1" *ngIf="!agent">Select an agent to view their monthly attendance grid</p>
    </div>

    <!-- Agent Dropdown Selector -->
    <div class="w-full md:w-72">
      <label class="block text-xs font-semibold text-gray-500 uppercase mb-1">Select Agent</label>
      <select [(ngModel)]="selectedAgentId" (change)="onAgentChange()" class="w-full border border-gray-300 rounded-lg p-2.5 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500">
        <option value="">-- Choose Agent --</option>
        <option *ngFor="let a of agents" [value]="a.agentId">{{ a.name }} ({{ a.phoneNumber }})</option>
      </select>
    </div>
  </div>

  <div class="grid grid-cols-1 lg:grid-cols-3 gap-6" *ngIf="agent">
    <!-- Calendar Grid Panel (2/3) -->
    <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-6 lg:col-span-2">
      <!-- Calendar Navigator Header -->
      <div class="flex items-center justify-between mb-6 pb-4 border-b border-gray-100">
        <button (click)="prevMonth()" class="p-2 rounded-lg hover:bg-gray-100 text-gray-600 transition-colors">
          <i class="fas fa-chevron-left text-lg"></i>
        </button>
        <h2 class="text-lg font-bold text-gray-800">
          {{ monthName }} {{ currentYear }}
        </h2>
        <button (click)="nextMonth()" class="p-2 rounded-lg hover:bg-gray-100 text-gray-600 transition-colors">
          <i class="fas fa-chevron-right text-lg"></i>
        </button>
      </div>

      <!-- Loading Placeholder -->
      <div class="text-center p-12" *ngIf="isLoading">
        <i class="fas fa-spinner fa-spin text-3xl text-green-600"></i>
        <p class="text-xs text-gray-400 mt-2">Loading attendance logs...</p>
      </div>

      <!-- Calendar Table Grid -->
      <div *ngIf="!isLoading">
        <!-- Weekday Headers -->
        <div class="grid grid-cols-7 gap-2 text-center mb-2">
          <div *ngFor="let day of weekDays" class="text-xs font-semibold uppercase text-gray-400 py-2">
            {{ day }}
          </div>
        </div>

        <!-- Day Cells Grid -->
        <div class="grid grid-cols-7 gap-2">
          <div *ngFor="let day of calendarDays" 
               (click)="day && isCellClickable(day) && toggleDateSelection(day.date)"
               class="aspect-square relative rounded-xl border flex flex-col justify-between p-2.5 transition-all duration-200"
               [ngClass]="{
                 'border-transparent bg-transparent': day === null,
                 'cursor-pointer': isCellClickable(day),
                 'border-emerald-500 ring-2 ring-emerald-500/20 bg-emerald-50/50 text-emerald-950 font-semibold shadow-sm': day !== null && isDateSelected(day.date),
                 'border-green-200 bg-green-50 text-green-800': day !== null && !isDateSelected(day.date) && day.status === 'Present',
                 'border-gray-200 bg-gray-50 text-gray-800': day !== null && !isDateSelected(day.date) && day.status === 'Holiday',
                 'border-blue-200 bg-blue-50 text-blue-800': day !== null && !isDateSelected(day.date) && day.status === 'PlannedLeave' && day.isStandard,
                 'border-sky-200 bg-sky-50 text-sky-800': day !== null && !isDateSelected(day.date) && day.status === 'PlannedLeave' && !day.isStandard,
                 'border-red-200 bg-red-50 text-red-800 hover:bg-red-100': day !== null && !isDateSelected(day.date) && day.status === 'UnplannedLeave',
                 'border-gray-200 bg-white text-gray-400 hover:bg-gray-50': day !== null && !isDateSelected(day.date) && (day.status === 'Future' || day.status === 'OffDuty')
               }">

            <div *ngIf="day !== null" class="flex justify-between items-start">
              <span class="font-bold text-sm">{{ day.date | date:'d' }}</span>

              <!-- Status Icon Indicators -->
              <span class="text-xs">
                <i *ngIf="isDateSelected(day.date)" class="fas fa-check-square text-emerald-600 text-sm" title="Selected for Leave"></i>
                <ng-container *ngIf="!isDateSelected(day.date)">
                  <i *ngIf="day.status === 'Present'" class="fas fa-check-circle text-green-600"></i>
                  <i *ngIf="day.status === 'Holiday'" class="fas fa-umbrella-beach text-gray-400"></i>
                  <i *ngIf="day.status === 'OffDuty'" class="fas fa-minus-circle text-gray-300" title="Off-Duty / Standby"></i>
                  <span *ngIf="day.status === 'PlannedLeave'" class="flex flex-col items-end gap-0.5">
                    <span class="flex items-center gap-0.5">
                      <i class="fas fa-plane text-blue-500 text-[10px]"></i>
                      <span class="text-[9px] font-bold px-1 rounded" [ngClass]="day.isStandard ? 'bg-blue-200 text-blue-800' : 'bg-sky-200 text-sky-800'">
                        {{ day.isStandard ? 'Standard' : 'Penalized' }}
                      </span>
                    </span>
                    <span *ngIf="day.isHalfDay" class="text-[9px] font-bold px-1 bg-amber-100 text-amber-800 rounded">
                      ½ Day
                    </span>
                  </span>
                  <i *ngIf="day.status === 'UnplannedLeave'" class="fas fa-exclamation-triangle text-red-600"></i>
                </ng-container>
              </span>
            </div>

            <!-- Description / Reason label -->
            <div *ngIf="day !== null && day.description" class="text-[10px] truncate w-full text-left font-medium opacity-80" [title]="day.description">
              {{ day.description }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Side Panel: Stats, Quota & Legend -->
    <div class="flex flex-col gap-6">
      <!-- Summary Stats Panel -->
      <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 class="text-lg font-semibold text-gray-800 mb-4 flex items-center">
          <i class="fas fa-chart-pie text-green-600 mr-2"></i> Monthly Summary
        </h3>
        <div class="grid grid-cols-2 gap-4">
          <div class="bg-green-50 border border-green-150 rounded-lg p-3">
            <div class="text-xs text-green-800 font-medium">Checked-in</div>
            <div class="text-2xl font-bold text-green-950 mt-1">{{ presentCount }} days</div>
          </div>
          <div class="bg-blue-50 border border-blue-150 rounded-lg p-3">
            <div class="text-xs text-blue-800 font-medium">Planned Leaves</div>
            <div class="text-2xl font-bold text-blue-950 mt-1">{{ plannedLeaveCount }} days</div>
          </div>
          <div class="bg-red-50 border border-red-150 rounded-lg p-3 col-span-2">
            <div class="text-xs text-red-800 font-medium">Unplanned Absences</div>
            <div class="text-2xl font-bold text-red-950 mt-1">{{ unplannedLeaveCount }} days</div>
          </div>
          <div class="bg-gray-50 border border-gray-150 rounded-lg p-3 col-span-2">
            <div class="text-xs text-gray-600 font-medium">Company Holidays</div>
            <div class="text-lg font-bold text-gray-800 mt-1">{{ holidayCount }} days</div>
          </div>
        </div>
      </div>

      <!-- Leave Quota & Settings panel -->
      <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 class="text-lg font-semibold text-gray-800 mb-4 flex items-center">
          <i class="fas fa-wallet text-green-600 mr-2"></i> Quota & Balances
        </h3>

        <div *ngIf="isLoadingQuota" class="text-center py-4 text-gray-400 italic">
          Loading quota details...
        </div>

        <div *ngIf="quota" class="space-y-3.5 text-sm">
          <div class="flex justify-between items-center py-2 border-b border-gray-100">
            <span class="text-gray-500 font-medium">Available Balance</span>
            <span class="font-bold text-gray-900 bg-green-100 text-green-800 px-2.5 py-0.5 rounded-full">{{ quota.availableLeavesBalance }} days</span>
          </div>
          <div class="flex justify-between items-center py-2 border-b border-gray-100">
            <span class="text-gray-500">Allowed Per Month</span>
            <span class="font-semibold text-gray-800">{{ quota.allowedLeavesPerMonth }} days</span>
          </div>
          <div class="flex justify-between items-center py-2 border-b border-gray-100">
            <span class="text-gray-500">Informed Leave Penalty</span>
            <span class="font-semibold text-gray-800">₹{{ quota.informedLeavePenaltyAmount }} / day</span>
          </div>
          <div class="flex justify-between items-center py-2">
            <span class="text-gray-500">Uninformed Leave Penalty</span>
            <span class="font-semibold text-red-600">₹{{ quota.uninformedLeavePenaltyAmount }} / day</span>
          </div>
        </div>
      </div>

      <!-- Calendar Legend panel -->
      <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 class="text-sm font-semibold text-gray-700 mb-3.5">Legend</h3>
        <div class="space-y-2.5 text-xs font-medium text-gray-600">
          <div class="flex items-center gap-2">
            <span class="w-4 h-4 rounded-md border border-green-200 bg-green-50"></span>
            <span>Present (Checked-in via selfie)</span>
          </div>
          <div class="flex items-center gap-2">
            <span class="w-4 h-4 rounded-md border border-gray-200 bg-gray-50"></span>
            <span>Holiday (Company holiday)</span>
          </div>
          <div class="flex items-center gap-2">
            <span class="w-4 h-4 rounded-md border border-gray-200 bg-white"></span>
            <span>No Duty</span>
          </div>
          <div class="flex items-center gap-2">
            <span class="w-4 h-4 rounded-md border border-blue-200 bg-blue-50"></span>
            <span>Planned Leave - Standard (Quota covered)</span>
          </div>
          <div class="flex items-center gap-2">
            <span class="w-4 h-4 rounded-md border border-sky-200 bg-sky-50"></span>
            <span>Planned Leave - Penalized (Exceeded quota)</span>
          </div>
          <div class="flex items-center gap-2">
            <span class="w-4 h-4 rounded-md border border-red-200 bg-red-50"></span>
            <span>Unplanned Leave (Uninformed absence)</span>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Empty State when no Agent is selected -->
  <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center my-8" *ngIf="!agent">
    <div class="max-w-md mx-auto">
      <i class="far fa-calendar-alt text-5xl text-gray-300 mb-4 block"></i>
      <h2 class="text-lg font-bold text-gray-800 mb-2">No Agent Selected</h2>
      <p class="text-gray-500 text-sm mb-6">Choose an agent from the dropdown at the top right to load their detailed attendance history, carry-forward quota settings, and holiday calendar.</p>
    </div>
  </div>

  <!-- Dialog: Mark Leave Modal (Style reference consistent with override config modal) -->
  <div *ngIf="showMarkLeaveModal" class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in p-4">
    <div class="bg-white rounded-xl shadow-2xl border border-gray-100 max-w-md w-full overflow-hidden transform transition-all duration-300 scale-100">
      <!-- Modal Header with gradient -->
      <div class="bg-gradient-to-r from-emerald-600 to-teal-800 text-white p-5 flex justify-between items-center">
        <h3 class="text-lg font-bold">Mark Leave for Agent</h3>
        <button (click)="closeMarkLeaveModal()" class="text-white/80 hover:text-white bg-white/10 hover:bg-white/20 w-8 h-8 rounded-full flex items-center justify-center transition-all duration-200">
          <i class="fas fa-times"></i>
        </button>
      </div>

      <!-- Modal Body -->
      <div class="p-6 space-y-4">
        <div>
          <label class="block text-sm font-semibold text-gray-700 mb-1.5">Agent Name</label>
          <div class="bg-gray-50 border border-gray-150 rounded-lg p-2.5 text-gray-800 font-semibold text-sm">
            {{ agent?.name }} (ID: {{ agent?.agentId }})
          </div>
        </div>

        <div *ngIf="selectedDates.length > 0">
          <label class="block text-sm font-semibold text-gray-700 mb-1.5">Selected Dates ({{ selectedDates.length }})</label>
          <div class="flex flex-wrap gap-2 max-h-28 overflow-y-auto p-3 border-2 border-gray-100 rounded-lg bg-gray-50 text-sm">
            <span *ngFor="let d of selectedDates" class="bg-emerald-50 text-emerald-800 border border-emerald-200 px-2.5 py-1 rounded-md text-xs font-bold shadow-sm">
              {{ d | date:'mediumDate' }}
            </span>
          </div>
        </div>

        <div *ngIf="selectedDates.length === 0">
          <label class="block text-sm font-semibold text-gray-700 mb-1.5">Leave Date</label>
          <input type="date" [(ngModel)]="leaveDate" class="w-full border-2 border-gray-100 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-50 rounded-lg p-2.5 text-gray-700 focus:outline-none transition-all duration-200" />
        </div>

        <div>
          <label class="block text-sm font-semibold text-gray-700 mb-1.5">Leave Type</label>
          <div class="flex gap-4">
            <label class="flex items-center gap-2 cursor-pointer text-sm font-medium text-gray-700">
              <input type="radio" name="leaveType" [value]="false" [(ngModel)]="leaveIsHalfDay" class="w-4 h-4 text-emerald-600 focus:ring-emerald-500" />
              Full Day
            </label>
            <label class="flex items-center gap-2 cursor-pointer text-sm font-medium text-gray-700">
              <input type="radio" name="leaveType" [value]="true" [(ngModel)]="leaveIsHalfDay" class="w-4 h-4 text-emerald-600 focus:ring-emerald-500" />
              Half Day
            </label>
          </div>
        </div>

        <div>
          <label class="block text-sm font-semibold text-gray-700 mb-1.5">Leave Classification</label>
          <div class="flex gap-4">
            <label class="flex items-center gap-2 cursor-pointer text-sm font-medium text-gray-700">
              <input type="radio" name="leaveClassification" [value]="false" [(ngModel)]="leaveIsUnplanned" class="w-4 h-4 text-emerald-600 focus:ring-emerald-500" />
              Planned Leave
            </label>
            <label class="flex items-center gap-2 cursor-pointer text-sm font-medium text-gray-700">
              <input type="radio" name="leaveClassification" [value]="true" [(ngModel)]="leaveIsUnplanned" class="w-4 h-4 text-emerald-600 focus:ring-emerald-500" />
              Unplanned Leave (No-Show)
            </label>
          </div>
        </div>

        <div>
          <label class="block text-sm font-semibold text-gray-700 mb-1.5">Reason</label>
          <textarea [(ngModel)]="leaveReason" placeholder="Enter reason for leave..." rows="3" class="w-full border-2 border-gray-150 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-50 rounded-lg p-3 text-sm text-gray-700 focus:outline-none transition-all duration-200 resize-none"></textarea>
        </div>
      </div>

      <!-- Modal Footer -->
      <div class="bg-gray-50 px-6 py-4 flex justify-end gap-3 border-t border-gray-100">
        <button (click)="closeMarkLeaveModal()" class="px-5 py-2.5 border-2 border-gray-200 hover:border-gray-300 rounded-lg text-sm font-semibold text-gray-700 hover:bg-gray-100 transition-colors">
          Cancel
        </button>
        <button (click)="submitMarkLeave()" [disabled]="submittingLeave || (!leaveDate && selectedDates.length === 0) || !leaveReason.trim()" class="px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded-lg text-sm font-bold shadow-md transition-all duration-200">
          {{ submittingLeave ? 'Submitting...' : 'Mark Leave' }}
        </button>
      </div>
    </div>
  </div>

  <!-- Notification Toast -->
  <div *ngIf="notification.show" [ngClass]="{
    'bg-green-600': notification.type === 'success',
    'bg-red-600': notification.type === 'error',
    'bg-blue-600': notification.type === 'info'
  }" class="fixed bottom-5 right-5 z-50 text-white px-6 py-3.5 rounded-lg shadow-xl flex items-center transition-all duration-300">
    <i [ngClass]="{
      'fa-check-circle': notification.type === 'success',
      'fa-exclamation-circle': notification.type === 'error',
      'fa-info-circle': notification.type === 'info'
    }" class="fas mr-2"></i>
    <span>{{ notification.message }}</span>
  </div>
</div>
